import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used inside FavoritesProvider');
  return ctx;
};

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); setLoaded(true); return; }
    try {
      const { data } = await api.get('/me/favorites/ids');
      setIds(new Set(data.ids || []));
    } catch { setIds(new Set()); }
    setLoaded(true);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFavorite = useCallback((songId) => ids.has(songId), [ids]);

  const toggle = useCallback(async (song) => {
    if (!user) {
      toast.info('Sign in to save favourites');
      return;
    }
    // Optimistic update
    setIds((prev) => {
      const n = new Set(prev);
      if (n.has(song.id)) n.delete(song.id); else n.add(song.id);
      return n;
    });
    try {
      const { data } = await api.post(`/me/favorites/${song.id}`);
      toast.success(data.favorited ? 'Added to Meri Pasand ♥' : 'Removed from Meri Pasand');
    } catch {
      // Revert on failure
      setIds((prev) => {
        const n = new Set(prev);
        if (n.has(song.id)) n.delete(song.id); else n.add(song.id);
        return n;
      });
      toast.error('Could not update favourites');
    }
  }, [user]);

  return (
    <FavoritesContext.Provider value={{ ids, isFavorite, toggle, loaded, refresh }}>
      {children}
    </FavoritesContext.Provider>
  );
};
